import React, { useState } from 'react';
import API from '../../Services/api';
import { toast } from '../../Services/toast';

const defaultWeights = {
  'Mobile Phone': 0.2, 'Laptop': 2.5, 'Monitor': 5.0, 'Desktop PC': 8.0,
  'Tablet': 0.7, 'TV': 10.0, 'Printer': 4.0, 'Battery': 0.5,
  'Cables/Chargers': 0.3, 'Camera': 0.4, 'Gaming Console': 1.5,
  'Keyboard/Mouse': 0.8, 'Router/Modem': 0.5, 'Other': 1.0,
};

const deviceBasePoints = {
  'Mobile Phone': 60, 'Laptop': 200, 'Monitor': 150, 'Desktop PC': 250,
  'Tablet': 100, 'TV': 180, 'Printer': 80, 'Battery': 20,
  'Cables/Chargers': 15, 'Camera': 70, 'Gaming Console': 120,
  'Keyboard/Mouse': 25, 'Router/Modem': 40, 'Other': 35,
};

const conditionMultipliers = { New: 1.0, Used: 0.7, Damaged: 0.4 };

function calcPoints(item, collectionPoint) {
  const cpPoints = collectionPoint?.acceptedItems?.find(i => i.name === item.itemType)?.points;
  const base = deviceBasePoints[item.itemType] || cpPoints || 35;
  const mult = conditionMultipliers[item.condition] || 1;
  return Math.round(base * mult * (parseInt(item.quantity) || 1));
}

function calcEnvImpact(item) {
  const weight = parseFloat(item.weight) || defaultWeights[item.itemType] || 1;
  const qty = parseInt(item.quantity) || 1;
  const total = weight * qty;
  return {
    co2Saved: +(total * 3.5).toFixed(2),
    energySaved: +(total * 12).toFixed(1),
    landfillDiverted: +total.toFixed(2),
    treesEquivalent: +(total * 3.5 / 21).toFixed(2),
  };
}

function makeBlankItem() {
  return {
    itemType: '', weight: 0.5, quantity: 1, condition: 'New', brandModel: '',
    estimatedPoints: 0,
    environmentalImpact: { co2Saved: 0, energySaved: 0, landfillDiverted: 0, treesEquivalent: 0 },
  };
}

function RecyclingSubmissionPopup({ collectionPoint, closePopup, initialData, pendingItems }) {
  const buildInitialItems = () => {
    if (pendingItems && pendingItems.length > 0) return pendingItems;
    const base = { ...makeBlankItem(), ...(initialData || {}) };
    base.estimatedPoints = calcPoints(base, collectionPoint);
    base.environmentalImpact = calcEnvImpact(base);
    return [base];
  };

  const [items, setItems] = useState(buildInitialItems());
  const [showAuthGate, setShowAuthGate] = useState(false);

  const handleItemChange = (index, field, value) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [field]: value };
    if (field === 'itemType' && defaultWeights[value]) {
      updated[index].weight = defaultWeights[value];
    }
    updated[index].estimatedPoints = calcPoints(updated[index], collectionPoint);
    updated[index].environmentalImpact = calcEnvImpact(updated[index]);
    setItems(updated);
  };

  const handleAddItem = () => setItems([...items, makeBlankItem()]);

  const handleRemoveItem = (index) => {
    if (items.length === 1) return;
    setItems(items.filter((_, i) => i !== index));
  };

  const totalPoints = items.reduce((s, i) => s + i.estimatedPoints, 0);
  const totalTrees = items.reduce((s, i) => s + (i.environmentalImpact?.treesEquivalent || 0), 0);
  const totalCo2 = items.reduce((s, i) => s + (i.environmentalImpact?.co2Saved || 0), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');

    if (!token) {
      sessionStorage.setItem('pendingRecyclingState', JSON.stringify({ collectionPoint, items }));
      setShowAuthGate(true);
      return;
    }

    try {
      const recyclingHistory = items.map(item => ({
        deviceType: item.itemType,
        deviceCondition: item.condition,
        brandModel: item.brandModel,
        weight: item.weight,
        itemsRecycled: item.quantity,
        pointsEarned: item.estimatedPoints,
        environmentalImpact: item.environmentalImpact,
        quantity: item.quantity,
        status: 'Pending',
      }));

      const response = await API.post('/collection-points/add-recycling-entry', {
        collectionPointId: collectionPoint._id,
        recyclingHistory,
      });

      if (response.status === 200) {
        toast('♻️ Recycling request submitted! Redirecting to your dashboard…', 'success', 3000);
        closePopup();
        setTimeout(() => { window.location.href = '/profile'; }, 2000);
      } else {
        throw new Error('Failed to submit.');
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast('Your session has expired. Please log in again.', 'error');
        setTimeout(() => { window.location.href = '/login'; }, 2000);
        return;
      }
      toast('There was an issue with your submission. Please try again.', 'error');
    }
  };

  if (showAuthGate) {
    return (
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center" style={{ zIndex: 999 }}>
        <div className="bg-white p-8 rounded-2xl shadow-xl w-80 text-center space-y-5">
          <div className="text-5xl">♻️</div>
          <h3 className="text-lg font-bold text-[#172A3A]">Almost there!</h3>
          <p className="text-sm text-gray-600">
            You need an account to submit your recycling request. Your items are saved — you can pick up right where you left off after signing in or registering.
          </p>
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 space-y-1">
            <p className="text-sm font-semibold text-[#172A3A]">
              {items.length} item{items.length !== 1 ? 's' : ''} ready to submit
            </p>
            <p className="text-emerald-700 text-xs">+{totalPoints} pts · 🌳 {totalTrees.toFixed(2)} trees equivalent · 🌍 {totalCo2.toFixed(2)} kg CO₂ saved</p>
          </div>
          <div className="space-y-2">
            <a
              href="/register?from=recycling"
              className="block w-full bg-[#29752B] text-white py-2.5 rounded-xl font-semibold hover:bg-[#1e5c20] transition-colors"
            >
              Create a Free Account
            </a>
            <a
              href="/login?from=recycling"
              className="block w-full border border-[#29752B] text-[#29752B] py-2.5 rounded-xl font-semibold hover:bg-green-50 transition-colors"
            >
              Sign In
            </a>
          </div>
          <button
            onClick={() => setShowAuthGate(false)}
            className="text-sm text-gray-400 hover:text-gray-600 underline"
          >
            ← Go back to my items
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" style={{ zIndex: 999 }}>
      <div className="bg-white w-full sm:rounded-2xl shadow-2xl sm:max-w-lg md:max-w-2xl lg:max-w-3xl flex flex-col max-h-[95vh] sm:max-h-[88vh] rounded-t-2xl">

        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-4 pb-2.5 md:px-5 md:pt-5 md:pb-3 border-b border-gray-100 shrink-0">
          <div>
            <h4 className="text-[#34A853] text-base md:text-xl font-semibold leading-tight">Submit Recycling Request</h4>
            <p className="text-xs text-gray-500 mt-0.5">at <strong className="text-[#172A3A]">{collectionPoint.name}</strong></p>
          </div>
          <button onClick={closePopup} className="text-[#6E7D80] text-2xl font-bold hover:text-gray-700 leading-none w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors shrink-0">×</button>
        </div>

        {/* Trees hint banner */}
        <div className="flex items-center gap-2 bg-emerald-50 border-b border-emerald-100 px-4 py-2 md:px-5 md:py-2.5 shrink-0">
          <span className="text-sm shrink-0">🌳</span>
          <p className="text-xs text-emerald-700 leading-snug">
            <strong>Trees equivalent</strong> shows CO₂ savings. Add more devices to boost your impact!
          </p>
        </div>

        <form id="recycling-form" onSubmit={handleSubmit} className="flex flex-col md:flex-row flex-1 min-h-0 overflow-hidden">

          {/* Left / main — scrollable item cards */}
          <div className="flex-1 overflow-y-auto custom-scrollbar px-4 py-3 md:p-5 space-y-3 md:space-y-4">
            <p className="text-sm text-gray-600 hidden md:block mb-1">
              Select all the items you wish to recycle. You can add as many devices as you like — each one increases your environmental impact!
            </p>

            {items.map((item, index) => (
              <div key={index} className="p-3 md:p-4 border border-gray-200 rounded-xl space-y-2.5 md:space-y-3 relative bg-gray-50/50 hover:border-gray-300 transition-colors">
                {items.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveItem(index)}
                    className="absolute top-2.5 right-2.5 text-gray-300 hover:text-red-400 text-base font-bold w-6 h-6 flex items-center justify-center rounded-full hover:bg-red-50 transition-colors"
                    title="Remove this item"
                  >
                    ✕
                  </button>
                )}
                <p className="text-xs font-semibold text-[#34A853] uppercase tracking-widest">Item {index + 1}</p>

                <div>
                  <label className="block text-[#414042] text-sm font-medium mb-1">Item Type</label>
                  <select
                    className="w-full border border-gray-300 px-3 py-2.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#29752B]/30 focus:border-[#29752B] text-sm bg-white transition"
                    value={item.itemType}
                    onChange={(e) => handleItemChange(index, 'itemType', e.target.value)}
                    required
                  >
                    <option value="">Select item</option>
                    {collectionPoint.acceptedItems.map((ai) => (
                      <option key={ai.name} value={ai.name}>{ai.name}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[#414042] text-sm font-medium mb-1">Condition</label>
                    <select
                      className="w-full border border-gray-300 px-3 py-2.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#29752B]/30 focus:border-[#29752B] text-sm bg-white transition"
                      value={item.condition}
                      onChange={(e) => handleItemChange(index, 'condition', e.target.value)}
                    >
                      <option value="New">New</option>
                      <option value="Used">Used</option>
                      <option value="Damaged">Damaged</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[#414042] text-sm font-medium mb-1">Quantity</label>
                    <input
                      type="number"
                      min="1"
                      max="50"
                      value={item.quantity}
                      onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value) || 1)}
                      className="w-full border border-gray-300 px-3 py-2.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#29752B]/30 focus:border-[#29752B] text-sm bg-white transition"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[#414042] text-sm font-medium mb-1">
                    Brand / Model <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={item.brandModel}
                    placeholder="e.g. Apple iPhone 12"
                    onChange={(e) => handleItemChange(index, 'brandModel', e.target.value)}
                    className="w-full border border-gray-300 px-3 py-2.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#29752B]/30 focus:border-[#29752B] text-sm bg-white transition"
                  />
                </div>

                <div className="bg-green-50 border border-green-100 rounded-lg px-3 py-2.5 flex justify-between items-center">
                  <span className="text-sm text-[#414042] font-medium">Reward Points</span>
                  <span className="font-bold text-[#18421B]">+{item.estimatedPoints} pts</span>
                </div>

                <div className="grid grid-cols-4 gap-2 text-center">
                  <div className="bg-blue-50 rounded-xl p-2">
                    <div className="text-base mb-0.5">🌍</div>
                    <div className="font-bold text-blue-700 text-xs leading-tight">{item.environmentalImpact?.co2Saved ?? 0}<span className="font-normal text-[10px]"> kg</span></div>
                    <div className="text-[10px] text-blue-400 mt-0.5">CO₂</div>
                  </div>
                  <div className="bg-yellow-50 rounded-xl p-2">
                    <div className="text-base mb-0.5">⚡</div>
                    <div className="font-bold text-yellow-700 text-xs leading-tight">{item.environmentalImpact?.energySaved ?? 0}<span className="font-normal text-[10px]"> kWh</span></div>
                    <div className="text-[10px] text-yellow-500 mt-0.5">Energy</div>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-2">
                    <div className="text-base mb-0.5">🗑️</div>
                    <div className="font-bold text-amber-700 text-xs leading-tight">{item.environmentalImpact?.landfillDiverted ?? 0}<span className="font-normal text-[10px]"> kg</span></div>
                    <div className="text-[10px] text-amber-500 mt-0.5">Landfill</div>
                  </div>
                  <div className="bg-emerald-50 rounded-xl p-2 relative group cursor-help">
                    <div className="text-base mb-0.5">🌳</div>
                    <div className="font-bold text-emerald-700 text-xs leading-tight">{item.environmentalImpact?.treesEquivalent ?? 0}</div>
                    <div className="text-[10px] text-emerald-500 mt-0.5">Trees</div>
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded-xl px-3 py-2 w-52 z-10 shadow-xl pointer-events-none">
                      Number of trees that would absorb this much CO₂ in a year. Add more items to grow this!
                      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Add device button — visible in scroll area on mobile */}
            <button
              type="button"
              onClick={handleAddItem}
              className="w-full border-2 border-dashed border-[#34A853] text-[#34A853] font-medium py-3 rounded-xl hover:bg-green-50 transition-colors text-sm flex items-center justify-center gap-2"
            >
              <span className="text-lg font-bold leading-none">+</span> Add Another Device
            </button>
          </div>

          {/* Desktop sidebar — hidden on mobile */}
          <div className="hidden md:flex md:w-64 lg:w-72 shrink-0 border-l border-gray-100 bg-gray-50/60 flex-col p-5 gap-4">
            <div className="bg-gradient-to-br from-emerald-50 to-green-50 border border-emerald-200 rounded-2xl p-4 space-y-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Your total impact</p>
              <div className="flex justify-between items-center">
                <span className="text-sm text-[#172A3A] font-medium">{items.length} item{items.length !== 1 ? 's' : ''}</span>
                <span className="font-bold text-[#18421B] text-xl">+{totalPoints} <span className="text-sm font-semibold">pts</span></span>
              </div>
              <div className="border-t border-emerald-100 pt-2.5 grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-[10px] text-gray-400">CO₂ saved</p>
                  <p className="font-bold text-blue-700 text-sm">{totalCo2.toFixed(1)}<span className="text-[10px] font-normal"> kg</span></p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-400">Trees equiv.</p>
                  <p className="font-bold text-emerald-700 text-sm">🌳 {totalTrees.toFixed(1)}</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-400">Items</p>
                  <p className="font-bold text-[#172A3A] text-sm">{items.reduce((s, i) => s + i.quantity, 0)}</p>
                </div>
              </div>
            </div>
            <div className="flex-1" />
            <div className="space-y-2">
              <button
                type="submit"
                className="bg-[#34A853] text-white py-3 px-6 rounded-full w-full hover:bg-[#206A29] font-semibold transition-colors text-sm shadow-sm"
              >
                Submit Recycling Request
              </button>
              <p className="text-center text-[10px] text-gray-400">*Points are estimates. Final reward confirmed at the centre.</p>
            </div>
          </div>
        </form>

        {/* Mobile-only sticky footer — slim submit bar */}
        <div className="md:hidden shrink-0 border-t border-gray-100 bg-white px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="flex-1 bg-emerald-50 rounded-xl px-3 py-2">
              <p className="text-[10px] text-gray-400 leading-none mb-0.5">{items.length} item{items.length !== 1 ? 's' : ''} · 🌳 {totalTrees.toFixed(1)} trees</p>
              <p className="font-bold text-[#18421B] text-sm leading-none">+{totalPoints} pts</p>
            </div>
            <button
              type="submit"
              form="recycling-form"
              className="bg-[#34A853] text-white py-2.5 px-5 rounded-full font-semibold transition-colors text-sm shadow-sm hover:bg-[#206A29] shrink-0"
            >
              Submit
            </button>
          </div>
          <p className="text-center text-[10px] text-gray-400 mt-1.5">*Points are estimates. Final reward confirmed at the centre.</p>
        </div>

      </div>
    </div>
  );
}

export default RecyclingSubmissionPopup;
